import { Attr } from './attributes';

/**
 * Who is signed in, for client-side telemetry.
 *
 * The server learns identity from W3C baggage, injected by `apps/api`'s proxy, so
 * every api span carries org and user without a single route knowing about it. The
 * browser and the extension have no such channel — nothing upstream of them can
 * inject anything — so they hold it here and attach it at report time.
 *
 * That gap was measurable: over three days, 130 of 130 extension exceptions and 7 of
 * 7 web exceptions carried no org id, against 42 of 47 on the api. An error you
 * cannot attribute to a customer is a bug report without a reporter.
 *
 * Ids only. Never a name, never an email — the browser SDK's `getUser()` would send
 * both, and Firmable's PII rule forbids it. An org id is an identifier, not personal
 * data, which is why it belongs here while everything else about the org does not.
 *
 * A module-level holder is safe here in a way it is not on the server: the browser
 * and the MV3 worker each run one bundle, so there is only ever one copy. See
 * `reporter.ts` for why the same shape needed a global on the Next server.
 */
export interface TelemetryIdentity {
  /** Clerk user id (`user_…`). */
  userId?: string;
  /** Clerk organization id (`org_…`). */
  orgId?: string;
}

let identity: TelemetryIdentity = {};

/** Blank fields clear that half, so signing out of an org does not strand a stale id. */
export function setTelemetryIdentity(next: TelemetryIdentity): void {
  identity = { userId: next.userId || undefined, orgId: next.orgId || undefined };
}

export function clearTelemetryIdentity(): void {
  identity = {};
}

export function getTelemetryIdentity(): TelemetryIdentity {
  return identity;
}

/**
 * Identity as span attributes; empty when signed out.
 *
 * Emits the same `firmable.*` keys the api already uses, so one filter spans api,
 * web and extension. Before this, web wrote `user.id` and api wrote
 * `firmable.user_id`, and no single query could answer "what did this customer hit".
 */
export function identityAttributes(): Record<string, string> {
  const attributes: Record<string, string> = {};

  if (identity.userId) attributes[Attr.USER_ID] = identity.userId;
  if (identity.orgId) attributes[Attr.ORG_ID] = identity.orgId;

  return attributes;
}
